import { toAmount } from "./amount";

export interface OfferIntegrityInput {
	startupFee?: number | string | null;
	interestRate?: number | string | null;
	termFee?: number | string | null;
	effectiveInterestRate?: number | string | null;
	monthlyCost?: number | string | null;
	terms?: number | string | null;
	finalLoanAmount?: number | string | null;
	finalMaxAmount?: number | string | null;
	approvedCashAmount?: number | string | null;
	debtsToBeRedeemed?: number | string | null;
	existingLoanCapital?: number | string | null;
}

export interface OfferIntegrityOptions {
	/** Only the refinance task carries this; other steps cannot supply it. */
	appliedRefinance?: number | null;
}

const ROUNDING_TOLERANCE_PER_TERM = 0.01;

const AMOUNT_RECONCILIATION_TOLERANCE = 1;

export const findOfferIntegrityIssues = (
	offer: OfferIntegrityInput,
	{ appliedRefinance }: OfferIntegrityOptions = {}
): string[] => {
	const issues: string[] = [];

	const interestRate = toAmount(offer.interestRate);
	const effectiveInterestRate = toAmount(offer.effectiveInterestRate);
	const monthlyCost = toAmount(offer.monthlyCost);
	const terms = toAmount(offer.terms);
	const finalLoanAmount = toAmount(offer.finalLoanAmount);
	const finalMaxAmount = toAmount(offer.finalMaxAmount);
	const approvedCashAmount = toAmount(offer.approvedCashAmount);
	const debtsToBeRedeemed = toAmount(offer.debtsToBeRedeemed);
	const existingLoanCapital = toAmount(offer.existingLoanCapital);

	// Each step exposes a different offer subset, so only fields the step
	// actually carries are judged: an absent key is a schema difference, a
	// present-but-null key is missing data.
	const isNulled = (key: keyof OfferIntegrityInput) =>
		key in offer && toAmount(offer[key]) === null;

	if (isNulled("interestRate")) {
		issues.push("interestRate is missing");
	}

	if (isNulled("effectiveInterestRate")) {
		issues.push("effectiveInterestRate is missing");
	}

	if (isNulled("startupFee")) {
		issues.push("startupFee is missing");
	}

	if (isNulled("termFee")) {
		issues.push("termFee is missing");
	}

	if (isNulled("approvedCashAmount")) {
		issues.push("approvedCashAmount is missing");
	}

	if (
		interestRate !== null &&
		effectiveInterestRate !== null &&
		effectiveInterestRate < interestRate
	) {
		issues.push(
			`effectiveInterestRate (${effectiveInterestRate}) is lower than interestRate (${interestRate}), which suggests the two use different scales`
		);
	}

	if (
		monthlyCost !== null &&
		terms !== null &&
		terms > 0 &&
		finalLoanAmount !== null &&
		effectiveInterestRate !== null &&
		effectiveInterestRate > 0
	) {
		const totalRepaid = monthlyCost * terms;
		const tolerance = Math.max(1, terms * ROUNDING_TOLERANCE_PER_TERM);

		if (Math.abs(totalRepaid - finalLoanAmount) <= tolerance) {
			issues.push(
				`monthlyCost (${monthlyCost}) x terms (${terms}) equals finalLoanAmount (${finalLoanAmount}), so the instalments carry no interest or fees despite effectiveInterestRate being ${effectiveInterestRate}`
			);
		}
	}

	if (
		existingLoanCapital !== null &&
		debtsToBeRedeemed !== null &&
		existingLoanCapital - debtsToBeRedeemed > AMOUNT_RECONCILIATION_TOLERANCE
	) {
		issues.push(
			`existingLoanCapital (${existingLoanCapital}) exceeds debtsToBeRedeemed (${debtsToBeRedeemed}), which should include it`
		);
	}

	// A top-up's existing loan is redeemed on top of whatever the customer
	// applied to refinance, which only ever names their other debts.
	const internal = Math.min(
		Math.max(existingLoanCapital ?? 0, 0),
		Math.max(debtsToBeRedeemed ?? 0, 0)
	);

	const requested = toAmount(appliedRefinance);
	// A request the loan cannot hold means the approval was scaled down, which
	// says nothing about how that smaller loan splits.
	const isDownscaledApproval =
		requested !== null &&
		finalLoanAmount !== null &&
		approvedCashAmount !== null &&
		approvedCashAmount + internal + requested - finalLoanAmount >
			AMOUNT_RECONCILIATION_TOLERANCE;

	const applied = isDownscaledApproval ? null : requested;
	// Beyond that, the refinance inside the loan is the larger of what the
	// customer applied for and what else has to be redeemed.
	const refinanced =
		debtsToBeRedeemed === null
			? null
			: internal + Math.max(applied ?? 0, debtsToBeRedeemed - internal);

	// The cash paid out plus that refinance must add back up to the loan. Where
	// the applied refinance is unknown the sum is only a lower bound, so only
	// parts that outgrow the loan contradict it.
	if (
		finalLoanAmount !== null &&
		approvedCashAmount !== null &&
		refinanced !== null
	) {
		const difference = approvedCashAmount + refinanced - finalLoanAmount;

		if (
			(applied === null ? difference : Math.abs(difference)) >
			AMOUNT_RECONCILIATION_TOLERANCE
		) {
			issues.push(
				`approvedCashAmount (${approvedCashAmount}) + refinance (${refinanced}) does not equal finalLoanAmount (${finalLoanAmount})`
			);
		}
	}

	// A ceiling below the cash plus the refinance leaves the customer no way to
	// cover it, so the refinance step can never complete.
	if (
		finalMaxAmount !== null &&
		approvedCashAmount !== null &&
		refinanced !== null &&
		approvedCashAmount + refinanced - finalMaxAmount >
			AMOUNT_RECONCILIATION_TOLERANCE
	) {
		issues.push(
			`approvedCashAmount (${approvedCashAmount}) + refinance (${refinanced}) exceeds finalMaxAmount (${finalMaxAmount}), so the refinance cannot be covered`
		);
	}

	return issues;
};
